import React, { useEffect } from 'react';
import { makeStyles, Theme, createStyles } from '@material-ui/core/styles';
import Skeleton from '@material-ui/lab/Skeleton';
import {
  Drawer as BaseDrawer,
  Divider,
  Typography,
  Hidden,
  IconButton
} from '@material-ui/core';
import FolderIcon from '@material-ui/icons/Folder';
import InsertDriveFileIcon from '@material-ui/icons/InsertDriveFile';
import PerfectScrollbar from 'react-perfect-scrollbar';
import DrawerActions from './DrawerActions';
import {
  DirResource,
  FileResource,
  isDir,
  isFile,
  useResourceModal
} from '../../../../modules/resources';
import { useDirsActions } from '../../../../modules/dirs/hooks';
import useRouter from '../../../../utils/useRouter';
import { useSelector } from 'react-redux';
import { selectResource } from '../../../../modules/resources/selectors';
import { RootState } from '../../../../store';
import { DirectoryContents } from './DirectoryContents';
import { FileContents } from './FileContents';
import { Dir } from '../../../../modules/dirs';
import { File } from '../../../../modules/files';
import { useFilesActions } from '../../../../modules/files/hooks';
import { ResourceModal } from '../../../../modules/ui';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';

const useStyles = (options: DrawerOptions) =>
  makeStyles((theme: Theme) =>
    createStyles({
      drawer: {},
      content: {
        [theme.breakpoints.down('xs')]: {
          paddingBottom: theme.spacing(8)
        }
      },
      backButton: {
        marginLeft: theme.spacing(-1.5)
      },
      title: {
        display: 'flex',
        flexFlow: 'row nowrap',
        justifyContent: 'center',
        alignItems: 'center'
      },
      label: {
        marginLeft: theme.spacing(1.5),
        maxWidth: 165
      },
      drawerPaper: {
        position: 'fixed',
        top: options.offsetTop,
        height: `calc(100vh - ${options.offsetTop}px)`,
        width: '100%',
        [theme.breakpoints.up('sm')]: {
          width: options.width
        },
        zIndex: 990
      },
      drawerHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: theme.spacing(0, 1, 0, 2),
        ...theme.mixins.toolbar
      }
    })
  );

interface DrawerOptions {
  width: number;
  offsetTop: number;
}

interface Props {
  open: boolean;
  options: DrawerOptions;
  onClose: () => void;
  resource: DirResource | FileResource;
}

function Drawer({ open, onClose, options, resource: selectedResource }: Props) {
  const classes = useStyles(options)();
  const router = useRouter();
  const { deleteDir } = useDirsActions();
  const { deleteFile } = useFilesActions();
  const resource = useSelector((state: RootState) =>
    selectResource(state, selectedResource.id, selectedResource.type)
  );
  const { openModal } = useResourceModal();

  useEffect(() => {
    if (!resource) {
      onClose();
    }
    // eslint-disable-next-line
  }, [resource]);

  const handleDelete = () => {
    if (isDir(selectedResource)) {
      const dir = resource as Dir;
      deleteDir(dir);
      onClose();

      setTimeout(() => {
        if (dir.parent && dir.parent.id) {
          router.history.push(`/directory/${dir.parent.id}`);
        } else {
          router.history.push('/');
        }
      }, 300);
    }

    if (isFile(selectedResource)) {
      const file = resource as File;
      deleteFile(file);
      onClose();
    }
  };

  const handleRename = () => {
    openModal(ResourceModal.Edit, selectedResource);
  };

  const handleMove = () => {
    openModal(ResourceModal.Move, selectedResource);
  };

  const renderIcon = () => {
    if (isDir(selectedResource)) {
      return <FolderIcon />;
    }

    if (isFile(selectedResource)) {
      return <InsertDriveFileIcon />;
    }

    return null;
  };

  const renderResourceLabel = () => {
    if (!resource) {
      return null;
    }

    if (isDir(selectedResource)) {
      return resource.title;
    }

    if (isFile(selectedResource)) {
      return resource.filename;
    }

    return null;
  };

  const navigateToDirectory = (dir: Dir) => {
    router.history.push(`/directory/${dir.id}`);
    onClose();
  };

  return (
    <BaseDrawer
      className={classes.drawer}
      variant="persistent"
      anchor="right"
      open={open}
      classes={{
        paper: classes.drawerPaper
      }}>
      <div className={classes.drawerHeader}>
        <Hidden smUp>
          <IconButton
            className={classes.backButton}
            color="inherit"
            onClick={onClose}>
            <ChevronLeftIcon />
          </IconButton>
        </Hidden>
        <div className={classes.title}>
          {renderIcon()}
          <Typography noWrap variant="h6" className={classes.label}>
            {!resource ? <Skeleton width={100} /> : renderResourceLabel()}
          </Typography>
        </div>
        <DrawerActions
          onClose={onClose}
          onDelete={handleDelete}
          onRename={handleRename}
          onMove={handleMove}
        />
      </div>
      <Divider />
      <PerfectScrollbar>
        <div className={classes.content}>
          {resource && isDir(selectedResource) && (
            <DirectoryContents
              dir={resource as Dir}
              onNavigateClick={navigateToDirectory}
            />
          )}
          {resource && isFile(selectedResource) && (
            <FileContents file={resource as File} />
          )}
        </div>
      </PerfectScrollbar>
    </BaseDrawer>
  );
}

export default Drawer;
